import { useCallback, useState } from "react";
import { Clock3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AcceptWithoutEvidenceDialog } from "@/features/credentials/components/primary-source-runs/run-report-dialog/accept-without-evidence-dialog";
import { ReopenCarriedDecisionDialog } from "@/features/credentials/components/primary-source-runs/run-report-dialog/reopen-carried-decision-dialog";
import { useReportReview } from "@/features/credentials/components/primary-source-runs/run-report-dialog/use-report-review";
import type {
  ReportItem,
  RunScope,
} from "@/features/credentials/components/primary-source-runs/types";
import type { WorkspaceProvider } from "../../workspace-types";
import type { ProviderPageSearch } from "../provider-page-search";
import type { useProviderVerification } from "../use-provider-verification";
import { ReportSectionView } from "./report-section-view";
import { RunHistorySection } from "./run-history-section";
import { SectionHeading } from "./section-heading";
import { SectionNav, type SectionNavEntry } from "./section-nav";
import { SummaryStrip } from "./summary-strip";
import { VerificationReportSkeleton } from "./verification-report-skeleton";
import { VerificationNotices } from "./verification-notices";
import {
  ITEM_SECTIONS,
  itemSectionId,
  itemsBySection,
  OVERVIEW_SECTION,
  RUN_HISTORY_SECTION,
  sectionStatus,
  VerificationSectionId,
} from "./verification-sections";

function selectedSection({
  requested,
  available,
}: {
  requested?: VerificationSectionId;
  available: Set<VerificationSectionId>;
}) {
  return requested && available.has(requested)
    ? requested
    : VerificationSectionId.Overview;
}

export function VerificationTab({
  provider,
  scope,
  verification,
  search,
  onSearchChange,
  onRunVerification,
}: {
  provider: WorkspaceProvider;
  scope: RunScope;
  verification: ReturnType<typeof useProviderVerification>;
  search: ProviderPageSearch;
  onSearchChange: (changes: Partial<ProviderPageSearch>) => void;
  onRunVerification: () => void;
}) {
  const { history, runs, latestRun, report } = verification;
  const data = report.data;
  const canEdit = provider.capabilities.canEditCredentials;
  // Any run other than the latest, including a cancelled one, is history. The
  // API rejects review writes on it, and the summary strip keeps describing
  // the latest run rather than the report on screen.
  const isHistorical =
    !!data && !!history.data && data.run.id !== latestRun?.id;
  const canEditReport = canEdit && !isHistorical && data?.isLatestRun === true;
  const review = useReportReview({ data, scope, canEdit: canEditReport });
  // The item opened from the overview, brought into view in its section.
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const clearFocus = useCallback(() => setFocusKey(null), []);
  const grouped = data
    ? itemsBySection({ items: data.items, checks: data.checks })
    : undefined;
  // Only cancelled runs leave nothing to show by default.
  const hasRun = !!latestRun || !!search.run;
  const isLoading = history.isPending || (hasRun && report.isPending);
  const entries: SectionNavEntry[] = [{ section: OVERVIEW_SECTION }];
  const available = new Set<VerificationSectionId>([
    VerificationSectionId.Overview,
    VerificationSectionId.RunHistory,
  ]);
  for (const section of ITEM_SECTIONS) {
    const items = grouped?.get(section.id) ?? [];
    // Which sections exist is unknown while loading. Honoring the requested
    // one keeps the nav from highlighting Overview until the data lands.
    if (items.length > 0 || isLoading) {
      available.add(section.id);
    }
    if (items.length > 0) {
      entries.push({ section, status: sectionStatus(items) });
    }
  }
  entries.push({ section: RUN_HISTORY_SECTION });
  const selected = selectedSection({ requested: search.section, available });
  const psv = provider.psvStatus;

  function openItem(item: ReportItem) {
    if (!data) {
      return;
    }
    review.expand(item);
    setFocusKey(item.key);
    onSearchChange({ section: itemSectionId({ item, checks: data.checks }) });
  }

  /** Overview rows hide their details, so the upload control only exists in
   * the item's own section. The row is not focused as `openItem` does: that
   * would take focus back from the upload control. */
  function uploadEvidence() {
    const item = review.confirmItem;
    if (item && data && selected === VerificationSectionId.Overview) {
      onSearchChange({ section: itemSectionId({ item, checks: data.checks }) });
    }
    review.requestUpload();
  }

  function renderContent() {
    if (selected === VerificationSectionId.RunHistory) {
      return (
        <div className="space-y-4">
          <SectionHeading section={RUN_HISTORY_SECTION} />
          <RunHistorySection
            history={history}
            runs={runs}
            onViewReport={(runId) =>
              onSearchChange({
                run: runId,
                section: undefined,
                notice: undefined,
              })
            }
          />
        </div>
      );
    }
    if (isLoading) {
      return <VerificationReportSkeleton />;
    }
    if (history.isError) {
      return (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>Couldn’t load verification runs.</p>
          <Button
            variant="outline"
            disabled={history.isFetching}
            onClick={() => void history.refetch()}
          >
            Retry runs
          </Button>
        </div>
      );
    }
    if (report.isError) {
      return (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>Couldn’t load this report. Your saved run is still available.</p>
          <Button
            variant="outline"
            disabled={report.isFetching}
            onClick={() => void report.refetch()}
          >
            Retry report
          </Button>
        </div>
      );
    }
    if (!data || !grouped) {
      return <NotRunState canStart={canEdit} onRun={onRunVerification} />;
    }
    return (
      <ReportSectionView
        data={data}
        scope={scope}
        sectionId={selected}
        items={grouped.get(selected) ?? []}
        canEdit={canEditReport}
        review={review}
        focusKey={focusKey}
        onOpenItem={openItem}
        onFocused={clearFocus}
      />
    );
  }

  return (
    <div className="space-y-4" onKeyDown={review.onKeyDown}>
      {psv && (
        <SummaryStrip
          psv={psv}
          // The report's own summary updates as soon as a decision is made;
          // the history's catches up when it refetches.
          itemSummary={
            isHistorical ? latestRun?.itemSummary : data?.itemSummary
          }
        />
      )}
      <VerificationNotices
        data={data}
        notice={search.notice}
        isHistorical={isHistorical}
        canEdit={canEdit}
        onViewLatest={() => onSearchChange({ run: undefined })}
      />
      <div className="overflow-hidden rounded-xl border bg-background lg:flex">
        <SectionNav
          entries={entries}
          selected={selected}
          itemsLoading={isLoading}
          onSelect={(section) =>
            onSearchChange({
              section:
                section === VerificationSectionId.Overview
                  ? undefined
                  : section,
            })
          }
        />
        <div className="min-w-0 flex-1 p-4 sm:p-6">{renderContent()}</div>
      </div>
      <AcceptWithoutEvidenceDialog
        open={review.confirmItem !== null}
        onUpload={uploadEvidence}
        onAcceptAnyway={review.acceptAnyway}
        onCancel={review.cancelConfirm}
      />
      {review.reopenItem && (
        <ReopenCarriedDecisionDialog
          onReopen={review.reopen}
          onCancel={review.cancelReopen}
        />
      )}
    </div>
  );
}

const NOT_RUN_DESCRIPTION = "Ready to verify";

function NotRunState({
  canStart,
  onRun,
}: {
  canStart: boolean;
  onRun: () => void;
}) {
  return (
    <div className="space-y-6">
      <SectionHeading
        section={OVERVIEW_SECTION}
        description={NOT_RUN_DESCRIPTION}
      />
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <Clock3 className="size-8 text-muted-foreground" aria-hidden />
        <p className="text-lg font-medium">No verification run yet</p>
        <p className="text-sm text-muted-foreground">Last completed on: —</p>
        {canStart && <Button onClick={onRun}>Run verification</Button>}
      </div>
    </div>
  );
}
